import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { getSectionById } from '../data/newCurriculum';
import { getLessonContent } from '../data/allLessonContents';
import { getLevel as getStaticLevel } from '../data/kariyerCurriculum';
import { getLessonContent as getStaticKariyerLessonContent } from '../data/kariyerContents';
import { fetchLevel, fetchLessonContent as fetchApiLessonContent } from '../services/curriculumService';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  X, Heart, Zap, ChevronRight, Star,
  CheckCircle2, RotateCcw, ArrowLeft
} from 'lucide-react';
import { MultipleChoice, FillInBlank, DragDropSentence, MatchPairs, VideoLesson, FlashCard, VocabularyTimeRace, SwipeVocabularyCard } from '../components/exercises';
import Teaching from '../components/exercises/Teaching';
import SpeakingPractice from '../components/exercises/SpeakingPractice';
import SimpleSpeaking from '../components/exercises/SimpleSpeaking';
import VisualCards from '../components/exercises/VisualCards';
import StoryMode from '../components/exercises/StoryMode';
import DialogueSimulation from '../components/exercises/DialogueSimulation';
import WordHunt from '../components/exercises/WordHunt';
import MemoryTrick from '../components/exercises/MemoryTrick';
import SoundSettings from '../components/SoundSettings';
import { useSounds } from '../hooks/useSounds';
import axios from 'axios';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DuolingoLesson() {
  const { sectionId, levelId, unitId, lessonId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { playLessonComplete, playLevelComplete, playXpGain, playCorrect, playIncorrect, playCombo, playPerfect } = useSounds();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [hearts, setHearts] = useState(5);
  const [xpEarned, setXpEarned] = useState(0);
  const [exercises, setExercises] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(0); // Ardışık doğru cevap sayısı
  const [level, setLevel] = useState(null);
  const [lessonContent, setLessonContent] = useState(null);
  const [completionCount, setCompletionCount] = useState(0); // Tekrar sayısı

  // Route parametreleri değiştiğinde tüm state'leri sıfırla
  useEffect(() => {
    setCurrentStep(0);
    setHearts(5);
    setXpEarned(0);
    setExercises([]);
    setIsComplete(false);
    setShowResults(false);
    setCorrectAnswers(0);
    setWaitingForNext(false);
    setLoading(true);
    setStreak(0);
    setLevel(null);
    setLessonContent(null);
  }, [levelId, unitId, lessonId]);

  // currentStep değiştiğinde waitingForNext'i sıfırla
  useEffect(() => {
    setWaitingForNext(false);
  }, [currentStep]);

  // Kariyer Online veya eski sistem için uyumluluk
  // levelId sayı veya "level_X" formatında olabilir - her ikisi de Kariyer sistemi
  const isKariyer = levelId && (levelId.startsWith('level_') || !isNaN(parseInt(levelId)));
  
  // Level ve ders içeriğini yükle
  useEffect(() => {
    const loadData = async () => {
      if (!isKariyer) {
        setLoading(false);
        return;
      }

      try {
        // Önce API'den dene
        const apiLevel = await fetchLevel(levelId, 'english');
        if (apiLevel) {
          setLevel(apiLevel);
        } else {
          // Fallback: Static dosyadan yükle
          setLevel(getStaticLevel(levelId));
        }

        // Ders içeriğini yükle
        const apiContent = await fetchApiLessonContent(levelId, unitId, lessonId);
        if (apiContent) {
          setLessonContent(apiContent);
        } else {
          // Fallback: Static dosyadan yükle
          setLessonContent(getStaticKariyerLessonContent(levelId, unitId, lessonId));
        }
      } catch (error) {
        console.log('API error, falling back to static');
        setLevel(getStaticLevel(levelId));
        setLessonContent(getStaticKariyerLessonContent(levelId, unitId, lessonId));
      }
    };

    loadData();
  }, [levelId, unitId, lessonId, isKariyer]);
  
  const section = !isKariyer ? getSectionById(sectionId || levelId) : null;
  
  const unit = isKariyer 
    ? level?.units?.find(u => u.id === parseInt(unitId))
    : section?.units?.find(u => u.id === parseInt(unitId));
  const lesson = unit?.lessons?.find(l => l.id === parseInt(lessonId));
  
  // Eski sistemde static content kullan, Kariyer sistemde API'den gelen content kullan
  const oldLessonContent = !isKariyer ? getLessonContent(unitId, lessonId) : null;
  const activeLessonContent = isKariyer ? lessonContent : oldLessonContent;

  // Sonraki dersi bul
  // Sonraki ders bilgisini hesapla - useMemo ile cache'le
  const nextLessonInfo = useMemo(() => {
    if (!unit || !lesson) return null;
    
    const currentLessonIndex = unit.lessons.findIndex(l => l.id === parseInt(lessonId));
    
    // Aynı ünitede sonraki ders var mı?
    if (currentLessonIndex < unit.lessons.length - 1) {
      const nextLesson = unit.lessons[currentLessonIndex + 1];
      return {
        lesson: nextLesson,
        unitId: unit.id,
        unitTitle: unit.title,
        isNewUnit: false,
        isUnitComplete: false
      };
    }
    
    // Ünite bitti, sonraki üniteye bak
    const allUnits = isKariyer ? level?.units : section?.units;
    if (!allUnits) return null;
    
    const currentUnitIndex = allUnits.findIndex(u => u.id === parseInt(unitId));
    
    if (currentUnitIndex < allUnits.length - 1) {
      const nextUnit = allUnits[currentUnitIndex + 1];
      if (nextUnit?.lessons?.length > 0) {
        return {
          lesson: nextUnit.lessons[0],
          unitId: nextUnit.id,
          unitTitle: nextUnit.title,
          isNewUnit: true,
          isUnitComplete: true
        };
      }
    }
    
    // Tüm üniteler bitti
    return {
      isLevelComplete: true,
      isUnitComplete: true
    };
  }, [unitId, lessonId, unit, lesson, isKariyer, level, section]);

  // Sonraki derse git
  const goToNextLesson = () => {
    if (!nextLessonInfo || nextLessonInfo.isLevelComplete) {
      // Full page reload to reset all state
      window.location.href = isKariyer ? `/level/${levelId}` : `/learn/${sectionId}`;
      return;
    }
    
    if (isKariyer) {
      // Full page reload to ensure component remounts with fresh state
      window.location.href = `/lesson/${levelId}/${nextLessonInfo.unitId}/${nextLessonInfo.lesson.id}`;
    } else {
      window.location.href = `/learn/${sectionId}/lesson/${nextLessonInfo.unitId}/${nextLessonInfo.lesson.id}`;
    }
  };

  const generateExercisesFromContent = useCallback(() => {
    const generatedExercises = [];
    
    // YENİ: interview, theory, simulation tipleri - Mülakat eğitimi dersleri
    if ((activeLessonContent?.type === 'interview' || activeLessonContent?.type === 'theory' || activeLessonContent?.type === 'simulation') && activeLessonContent?.content) {
      const { sections, exercises } = activeLessonContent?.content;
      
      // Sections'ları işle
      if (sections && sections.length > 0) {
        sections.forEach(section => {
          // Intro tipi
          if (section.type === 'intro') {
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '🎯 Giriş',
                content: `<div class="prose prose-blue max-w-none"><p class="text-gray-700 whitespace-pre-line">${section.content}</p></div>`
              }
            });
          }
          // Info tipi
          else if (section.type === 'info') {
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '📋 Bilgi',
                content: `<div class="prose prose-blue max-w-none bg-blue-50 p-4 rounded-xl border border-blue-100"><p class="text-gray-700 whitespace-pre-line">${section.content}</p></div>`
              }
            });
          }
          // Scenario (mülakat diyaloğu) tipi
          else if (section.type === 'scenario' && section.dialogues) {
            let dialogueHtml = `<div class="space-y-4">`;
            section.dialogues.forEach(d => {
              const isMulakatci = d.speaker.includes('Mülakatçı') || d.speaker.includes('HR') || d.speaker.includes('Panel') || d.speaker.includes('Captain') || d.speaker.includes('Kaptan');
              const isAday = d.speaker.includes('Aday');
              const isTavsiye = d.speaker.includes('Tavsiye');
              const isKotu = d.speaker.includes('Kötü') || d.speaker.includes('Zayıf') || d.speaker.includes('Hazırlıksız');
              
              if (isTavsiye) {
                dialogueHtml += `
                  <div class="bg-green-50 border-l-4 border-green-500 p-3 rounded-r-xl">
                    <p class="text-green-700 text-sm">${d.text}</p>
                  </div>`;
              } else if (isKotu) {
                dialogueHtml += `
                  <div class="flex gap-3 justify-start">
                    <div class="bg-red-50 border border-red-200 rounded-2xl p-4 max-w-[85%]">
                      <p class="font-semibold text-sm text-red-600 mb-1">❌ ${d.speaker}</p>
                      <p class="text-gray-800">${d.text}</p>
                      ${d.translation ? `<p class="text-gray-500 text-sm mt-2 italic">${d.translation}</p>` : ''}
                    </div>
                  </div>`;
              } else if (isMulakatci) {
                dialogueHtml += `
                  <div class="flex gap-3 justify-start">
                    <div class="bg-purple-50 border border-purple-200 rounded-2xl p-4 max-w-[85%]">
                      <p class="font-semibold text-sm text-purple-600 mb-1">🎤 ${d.speaker}</p>
                      <p class="text-gray-800 font-medium">${d.text}</p>
                      ${d.translation ? `<p class="text-gray-500 text-sm mt-2 italic">${d.translation}</p>` : ''}
                    </div>
                  </div>`;
              } else if (isAday) {
                dialogueHtml += `
                  <div class="flex gap-3 justify-end">
                    <div class="bg-blue-50 border border-blue-200 rounded-2xl p-4 max-w-[85%]">
                      <p class="font-semibold text-sm text-blue-600 mb-1">👤 ${d.speaker}</p>
                      <p class="text-gray-800">${d.text}</p>
                      ${d.translation ? `<p class="text-gray-500 text-sm mt-2 italic">${d.translation}</p>` : ''}
                    </div>
                  </div>`;
              } else {
                // Diğer speaker'lar (Ortam, Ekran, vb.)
                dialogueHtml += `
                  <div class="bg-gray-100 p-3 rounded-xl text-center">
                    <p class="text-gray-600 text-sm font-medium">${d.speaker}</p>
                    <p class="text-gray-700">${d.text}</p>
                    ${d.translation ? `<p class="text-gray-500 text-sm mt-1 italic">${d.translation}</p>` : ''}
                  </div>`;
              }
            });
            dialogueHtml += `</div>`;
            
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '🎭 Mülakat Senaryosu',
                content: dialogueHtml
              }
            });
          }
          // Analysis tipi
          else if (section.type === 'analysis') {
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '📊 Analiz',
                content: `<div class="bg-green-50 border border-green-200 p-4 rounded-xl"><p class="text-gray-700 whitespace-pre-line">${section.content}</p></div>`
              }
            });
          }
          // Warning tipi
          else if (section.type === 'warning') {
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '⚠️ Uyarı',
                content: `<div class="bg-red-50 border border-red-200 p-4 rounded-xl"><p class="text-gray-700 whitespace-pre-line">${section.content}</p></div>`
              }
            });
          }
          // Framework tipi
          else if (section.type === 'framework') {
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '📋 Çerçeve',
                content: `<div class="bg-indigo-50 border border-indigo-200 p-4 rounded-xl"><p class="text-gray-700 whitespace-pre-line">${section.content}</p></div>`
              }
            });
          }
          // Checklist tipi
          else if (section.type === 'checklist') {
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '✅ Kontrol Listesi',
                content: `<div class="bg-amber-50 border border-amber-200 p-4 rounded-xl"><p class="text-gray-700 whitespace-pre-line">${section.content}</p></div>`
              }
            });
          }
          // Summary tipi
          else if (section.type === 'summary') {
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '📝 Özet',
                content: `<div class="bg-gray-50 border border-gray-200 p-4 rounded-xl"><p class="text-gray-700 whitespace-pre-line">${section.content}</p></div>`
              }
            });
          }
          // Tip tipi
          else if (section.type === 'tip') {
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: '💡 İpucu',
                content: `<div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-xl"><p class="text-gray-700">${section.content}</p></div>`
              }
            });
          }
        });
      }
      
      // Exercises'leri işle
      if (exercises && exercises.length > 0) {
        exercises.forEach(exercise => {
          if (exercise.type === 'multipleChoice') {
            generatedExercises.push({
              type: 'multipleChoice',
              data: {
                question: exercise.question,
                options: exercise.options,
                correctAnswer: exercise.correctIndex,
                explanation: exercise.explanation
              }
            });
          }
        });
      }
      
      return generatedExercises;
    }
    
    // YENİ: practice tipi - senaryo ve uygulama dersleri
    if (activeLessonContent?.type === 'practice' && activeLessonContent?.content) {
      const { sections, exercises } = activeLessonContent?.content;
      
      // Sections'ları işle (scenario, vocabulary, etc.)
      if (sections && sections.length > 0) {
        sections.forEach(section => {
          // Scenario (diyalog) tipi
          if (section.type === 'scenario' && section.dialogues) {
            let dialogueHtml = `<div class="space-y-4">`;
            section.dialogues.forEach(d => {
              const isPassenger = d.speaker === 'Yolcu' || d.speaker === 'Passenger';
              dialogueHtml += `
                <div class="flex gap-3 ${isPassenger ? 'justify-start' : 'justify-end'}">
                  <div class="${isPassenger ? 'bg-gray-100' : 'bg-blue-100'} rounded-2xl p-4 max-w-[80%]">
                    <p class="font-semibold text-sm ${isPassenger ? 'text-gray-600' : 'text-blue-600'} mb-1">
                      ${d.speaker}
                    </p>
                    <p class="text-gray-800 font-medium">${d.text}</p>
                    <p class="text-gray-500 text-sm mt-1 italic">${d.translation}</p>
                  </div>
                </div>`;
            });
            dialogueHtml += `</div>`;
            
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '✈️ Senaryo',
                content: dialogueHtml
              }
            });
          }
          // Vocabulary tipi
          else if (section.type === 'vocabulary' && section.words) {
            let wordsHtml = '<div class="grid grid-cols-1 md:grid-cols-2 gap-3">';
            section.words.forEach(word => {
              wordsHtml += `<div class="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <span class="font-bold text-blue-700">${word.en}</span>
                <span class="text-gray-400">→</span>
                <span class="text-gray-700">${word.tr}</span>
              </div>`;
            });
            wordsHtml += '</div>';
            
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '📚 Kelimeler',
                content: wordsHtml
              }
            });
          }
        });
      }
      
      // Exercises'leri işle
      if (exercises && exercises.length > 0) {
        exercises.forEach(exercise => {
          if (exercise.type === 'roleplay') {
            // Roleplay'i teaching olarak göster
            let roleplayHtml = `<div class="space-y-4">
              <p class="text-gray-600 mb-4">${exercise.instruction}</p>`;
            exercise.items?.forEach((item, idx) => {
              roleplayHtml += `
                <div class="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p class="font-medium text-amber-800 mb-2">📋 ${item.info}</p>
                  <details class="mt-2">
                    <summary class="cursor-pointer text-blue-600 text-sm">Cevabı Gör</summary>
                    <p class="mt-2 text-green-700 font-medium">"${item.answer}"</p>
                  </details>
                </div>`;
            });
            roleplayHtml += `</div>`;
            
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: '🎭 Uygulama',
                content: roleplayHtml
              }
            });
          } else if (exercise.type === 'fillBlank') {
            exercise.sentences?.forEach(sent => {
              generatedExercises.push({
                type: 'fillInBlank',
                data: {
                  sentence: sent.text,
                  correctAnswer: sent.answer,
                  hint: sent.hint
                }
              });
            });
          } else if (exercise.type === 'multipleChoice') {
            generatedExercises.push({
              type: 'multipleChoice',
              data: {
                question: exercise.question,
                options: exercise.options,
                correctAnswer: exercise.correctIndex,
                explanation: exercise.explanation
              }
            });
          }
        });
      }
      
      return generatedExercises;
    }
    
    // YENİ: lesson tipi - A0-C1 yeni müfredat yapısı
    if (activeLessonContent?.type === 'lesson' && activeLessonContent?.content) {
      const { sections, exercises } = activeLessonContent?.content;
      
      // Önce bölümleri Teaching olarak ekle
      if (sections && sections.length > 0) {
        sections.forEach(section => {
          if (section.type === 'grammar' || section.type === 'vocabulary' || section.type === 'tip') {
            // İçeriği HTML formatına çevir
            let htmlContent = section.explanation || section.content || '';
            
            // Examples (örnek listesi) varsa ekle
            if (section.examples && section.examples.length > 0) {
              let examplesHtml = '<ul class="list-disc list-inside mt-3 space-y-2">';
              section.examples.forEach(example => {
                examplesHtml += `<li class="text-gray-700">${example}</li>`;
              });
              examplesHtml += '</ul>';
              htmlContent += examplesHtml;
            }
            
            // Table varsa HTML table olarak ekle
            if (section.table) {
              const { headers, rows } = section.table;
              let tableHtml = '<table class="w-full border-collapse mt-4 mb-4"><thead><tr>';
              headers.forEach(h => {
                tableHtml += `<th class="border border-gray-300 bg-gray-100 px-3 py-2 text-left font-semibold">${h}</th>`;
              });
              tableHtml += '</tr></thead><tbody>';
              rows.forEach(row => {
                tableHtml += '<tr>';
                row.forEach(cell => {
                  tableHtml += `<td class="border border-gray-300 px-3 py-2">${cell}</td>`;
                });
                tableHtml += '</tr>';
              });
              tableHtml += '</tbody></table>';
              htmlContent += tableHtml;
            }
            
            // Words (kelime listesi) varsa liste olarak ekle
            if (section.words && section.words.length > 0) {
              let wordsHtml = '<div class="mt-4 space-y-2">';
              section.words.forEach(word => {
                wordsHtml += `<div class="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                  <span class="font-bold text-blue-700">${word.en}</span>
                  <span class="text-gray-400">→</span>
                  <span class="text-gray-700">${word.tr}</span>
                  ${word.pronunciation ? `<span class="text-sm text-gray-500">${word.pronunciation}</span>` : ''}
                </div>`;
              });
              wordsHtml += '</div>';
              htmlContent += wordsHtml;
            }
            
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '',
                content: htmlContent,
                tip: section.type === 'tip' ? section.content : null
              }
            });
          }
          // Scenario (diyalog) tipi - kabin memuru senaryoları için
          else if (section.type === 'scenario' && section.dialogues) {
            let dialogueHtml = `<div class="space-y-4">`;
            section.dialogues.forEach(d => {
              const isYolcu = d.speaker.includes('Yolcu') || d.speaker.includes('Passenger');
              const isKabinMemuru = d.speaker.includes('Kabin') || d.speaker.includes('Crew') || d.speaker.includes('Memur');
              
              if (isYolcu) {
                dialogueHtml += `
                  <div class="flex gap-3 justify-start">
                    <div class="bg-gray-100 border border-gray-200 rounded-2xl p-4 max-w-[85%]">
                      <p class="font-semibold text-sm text-gray-600 mb-1">👤 ${d.speaker}</p>
                      <p class="text-gray-800">${d.text}</p>
                      ${d.translation ? `<p class="text-gray-500 text-sm mt-2 italic">${d.translation}</p>` : ''}
                    </div>
                  </div>`;
              } else if (isKabinMemuru) {
                dialogueHtml += `
                  <div class="flex gap-3 justify-end">
                    <div class="bg-blue-50 border border-blue-200 rounded-2xl p-4 max-w-[85%]">
                      <p class="font-semibold text-sm text-blue-600 mb-1">✈️ ${d.speaker}</p>
                      <p class="text-gray-800">${d.text}</p>
                      ${d.translation ? `<p class="text-gray-500 text-sm mt-2 italic">${d.translation}</p>` : ''}
                    </div>
                  </div>`;
              } else {
                dialogueHtml += `
                  <div class="flex gap-3 justify-start">
                    <div class="bg-purple-50 border border-purple-200 rounded-2xl p-4 max-w-[85%]">
                      <p class="font-semibold text-sm text-purple-600 mb-1">💬 ${d.speaker}</p>
                      <p class="text-gray-800">${d.text}</p>
                      ${d.translation ? `<p class="text-gray-500 text-sm mt-2 italic">${d.translation}</p>` : ''}
                    </div>
                  </div>`;
              }
            });
            dialogueHtml += '</div>';
            
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title || '✈️ Senaryo',
                content: dialogueHtml
              }
            });
          }
        });
      }
      
      // Sonra egzersizleri ekle
      if (exercises && exercises.length > 0) {
        exercises.forEach(exercise => {
          if (exercise.type === 'matching' || exercise.type === 'matchPairs') {
            generatedExercises.push({
              type: 'matchPairs',
              data: {
                instruction: exercise.instruction || 'Eşleştirme yapın',
                pairs: exercise.pairs
              }
            });
          } else if (exercise.type === 'multipleChoice') {
            generatedExercises.push({
              type: 'multipleChoice',
              data: {
                question: exercise.question,
                options: exercise.options,
                correctAnswer: exercise.correctIndex !== undefined ? exercise.correctIndex : exercise.correct,
                explanation: exercise.explanation
              }
            });
          } else if (exercise.type === 'fillInBlank' || exercise.type === 'fill_blank') {
            generatedExercises.push({
              type: 'fillInBlank',
              data: {
                sentence: exercise.sentence,
                correctAnswer: exercise.answer || exercise.correct,
                hint: exercise.hint
              }
            });
          } else if (exercise.type === 'speaking') {
            generatedExercises.push({
              type: 'simpleSpeaking',
              data: {
                instruction: exercise.instruction,
                answer: exercise.answer,
                items: exercise.items
              }
            });
          } else if (exercise.type === 'dragDrop') {
            generatedExercises.push({
              type: 'dragDrop',
              data: {
                instruction: exercise.instruction || 'Kelimeleri doğru sıraya dizin',
                words: exercise.words,
                correctOrder: exercise.correctOrder,
                translation: exercise.translation
              }
            });
          } else if (exercise.type === 'flashcard') {
            generatedExercises.push({
              type: 'flashcard',
              data: {
                title: exercise.title || 'Kelime Kartları',
                cards: exercise.cards
              }
            });
          } else if (exercise.type === 'timeRace') {
            generatedExercises.push({
              type: 'timeRace',
              data: {
                title: exercise.title || 'Zaman Yarışı',
                cards: exercise.cards
              }
            });
          } else if (exercise.type === 'swipeVocabulary') {
            generatedExercises.push({
              type: 'swipeVocabulary',
              data: {
                title: exercise.title || 'Kelime Kartları',
                cards: exercise.cards
              }
            });
          }
        });
      }
      
      return generatedExercises;
    }
    
    // YENİ: mixed_lesson tipi - zengin içerikli dersler
    if (activeLessonContent?.type === 'mixed_lesson' && activeLessonContent?.exercises) {
      activeLessonContent?.exercises.forEach(exercise => {
        // Teaching (öğretme) bölümü
        if (exercise.type === 'teaching') {
          generatedExercises.push({
            type: 'teaching',
            data: {
              title: exercise.title,
              content: exercise.content,
              tip: exercise.tip
            }
          });
        }
        // Visual Cards - Görsel Kartlar
        else if (exercise.type === 'visualCards') {
          generatedExercises.push({
            type: 'visualCards',
            data: {
              title: exercise.title,
              cards: exercise.cards
            }
          });
        }
        // Story Mode - Hikaye Modu
        else if (exercise.type === 'storyMode') {
          generatedExercises.push({
            type: 'storyMode',
            data: {
              title: exercise.title,
              story: exercise.story,
              vocabulary: exercise.vocabulary
            }
          });
        }
        // Dialogue Simulation - Diyalog Simülasyonu
        else if (exercise.type === 'dialogueSimulation') {
          generatedExercises.push({
            type: 'dialogueSimulation',
            data: {
              title: exercise.title,
              scenario: exercise.scenario,
              dialogues: exercise.dialogues
            }
          });
        }
        // Word Hunt - Kelime Avı
        else if (exercise.type === 'wordHunt') {
          generatedExercises.push({
            type: 'wordHunt',
            data: {
              title: exercise.title,
              sentences: exercise.sentences,
              targetWords: exercise.targetWords
            }
          });
        }
        // Memory Trick - Hafıza Tekniği
        else if (exercise.type === 'memoryTrick') {
          generatedExercises.push({
            type: 'memoryTrick',
            data: {
              title: exercise.title,
              tricks: exercise.tricks
            }
          });
        }
        // FlashCard
        else if (exercise.type === 'flashcard') {
          generatedExercises.push({
            type: 'flashcard',
            data: {
              title: exercise.title || 'Kelime Kartları',
              cards: exercise.cards
            }
          });
        }
        // Multiple Choice
        else if (exercise.type === 'multipleChoice') {
          generatedExercises.push({
            type: 'multipleChoice',
            data: {
              question: exercise.question,
              options: exercise.options,
              correctAnswer: exercise.correct,
              explanation: exercise.explanation
            }
          });
        }
        // Fill in the Blank
        else if (exercise.type === 'fillInBlank') {
          generatedExercises.push({
            type: 'fillInBlank',
            data: {
              sentence: exercise.sentence,
              correctAnswer: exercise.answer,
              hint: exercise.hint
            }
          });
        }
        // Drag and Drop
        else if (exercise.type === 'dragDrop') {
          generatedExercises.push({
            type: 'dragDrop',
            data: {
              instruction: exercise.instruction,
              words: exercise.words,
              correctOrder: exercise.correctOrder,
              translation: exercise.translation
            }
          });
        }
        // Match Pairs
        else if (exercise.type === 'matchPairs') {
          generatedExercises.push({
            type: 'matchPairs',
            data: {
              instruction: exercise.instruction || 'Eşleştirme yapın',
              pairs: exercise.pairs
            }
          });
        }
      });
      return generatedExercises;
    }
    
    // YENİ: Exam (sınav) tipi - Her iki format da desteklenir
    if (activeLessonContent?.type === 'exam') {
      // Format 1: exercises array (eski format)
      // Format 2: content.questions array (yeni format)
      const exercises = activeLessonContent?.exercises || [];
      const questions = activeLessonContent?.content?.questions || [];
      
      // Önce exercises'i işle
      exercises.forEach(exercise => {
        if (exercise.type === 'multipleChoice') {
          generatedExercises.push({
            type: 'multipleChoice',
            data: {
              question: exercise.question,
              options: exercise.options,
              correctAnswer: exercise.correctIndex !== undefined ? exercise.correctIndex : exercise.correct,
              explanation: exercise.explanation
            }
          });
        } else if (exercise.type === 'fillInBlank') {
          generatedExercises.push({
            type: 'fillInBlank',
            data: {
              sentence: exercise.sentence,
              correctAnswer: exercise.answer,
              hint: exercise.hint
            }
          });
        } else if (exercise.type === 'matchPairs') {
          generatedExercises.push({
            type: 'matchPairs',
            data: {
              instruction: 'Eşleştirme yapın',
              pairs: exercise.pairs
            }
          });
        }
      });
      
      // Sonra content.questions'ı işle
      questions.forEach(q => {
        if (q.type === 'multipleChoice') {
          generatedExercises.push({
            type: 'multipleChoice',
            data: {
              question: q.question,
              options: q.options,
              correctAnswer: q.correctIndex !== undefined ? q.correctIndex : q.correct,
              explanation: q.explanation || `Doğru cevap: ${q.options[q.correctIndex !== undefined ? q.correctIndex : q.correct]}`
            }
          });
        } else if (q.type === 'fillInBlank' || q.type === 'fillBlank') {
          generatedExercises.push({
            type: 'fillInBlank',
            data: {
              sentence: q.question || q.sentence,
              correctAnswer: q.answer,
              hint: q.hint || ''
            }
          });
        }
      });
      
      if (generatedExercises.length > 0) {
        return generatedExercises;
      }
    }
    
    // Flashcard tipi için özel FlashCard komponenti
    if (activeLessonContent?.type === 'flashcard' && activeLessonContent?.cards) {
      generatedExercises.push({
        type: 'flashcard',
        data: {
          title: activeLessonContent?.title || 'Flash Kartlar',
          cards: activeLessonContent?.cards
        }
      });
      return generatedExercises;
    }
    
    // Flashcard içeriğinden egzersizler oluştur (eski yöntem - quiz tarzı)
    if (activeLessonContent?.cards && activeLessonContent?.type !== 'flashcard' && activeLessonContent?.type !== 'timerace') {
      // Çoktan seçmeli sorular
      activeLessonContent?.cards.slice(0, 3).forEach((card, idx) => {
        const wrongOptions = activeLessonContent?.cards
          .filter((_, i) => i !== idx)
          .slice(0, 3)
          .map(c => c.back);
        
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: `"${card.front}" kelimesinin karşılığı nedir?`,
            options: [...wrongOptions, card.back].sort(() => Math.random() - 0.5),
            correctAnswer: [...wrongOptions, card.back].sort(() => Math.random() - 0.5).indexOf(card.back),
            explanation: `Doğru cevap: ${card.back}`
          }
        });
      });

      // Eşleştirme egzersizi
      if (activeLessonContent?.cards.length >= 4) {
        generatedExercises.push({
          type: 'matchPairs',
          data: {
            instruction: 'İngilizce kelimeleri Türkçe karşılıklarıyla eşleştirin',
            pairs: activeLessonContent?.cards.slice(0, 5).map(card => ({
              left: card.front,
              right: card.back
            }))
          }
        });
      }
    }

    // Quiz içeriğinden egzersizler
    if (activeLessonContent?.questions) {
      activeLessonContent?.questions.forEach(q => {
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: q.question,
            options: q.options,
            correctAnswer: q.correct,
            explanation: q.explanation || ''
          }
        });
      });
    }

    // Voice/Roleplay içeriğinden boşluk doldurma
    if (activeLessonContent?.phrases) {
      activeLessonContent?.phrases.slice(0, 2).forEach(phrase => {
        const words = phrase.text.split(' ');
        if (words.length > 2) {
          const blankIndex = Math.floor(words.length / 2);
          const answer = words[blankIndex];
          const sentence = words.map((w, i) => i === blankIndex ? '___' : w).join(' ');
          
          generatedExercises.push({
            type: 'fillInBlank',
            data: {
              sentence: sentence,
              correctAnswer: answer,
              options: [answer, words[0], words[words.length - 1], 'the'].filter((v, i, a) => a.indexOf(v) === i).slice(0, 4),
              hint: phrase.translation
            }
          });
        }
      });
    }

    // Dialogue'dan sürükle bırak
    if (activeLessonContent?.dialogue) {
      const firstLine = activeLessonContent?.dialogue[0];
      if (firstLine?.english) {
        const words = firstLine.english.split(' ').slice(0, 5);
        if (words.length >= 3) {
          const shuffled = [...words].sort(() => Math.random() - 0.5);
          
          generatedExercises.push({
            type: 'dragDrop',
            data: {
              instruction: 'Kelimeleri doğru sıraya dizin',
              words: shuffled,
              correctOrder: shuffled.map(w => words.indexOf(w)),
              translation: firstLine.turkish
            }
          });
        }
      }
    }

    // Video içeriği
    if (activeLessonContent?.video) {
      generatedExercises.push({
        type: 'video',
        data: {
          title: activeLessonContent?.video.title || activeLessonContent?.title,
          videoUrl: activeLessonContent?.video.url,
          videoType: activeLessonContent?.video.type || 'youtube',
          description: activeLessonContent?.video.description,
          questions: activeLessonContent?.video.questions || []
        }
      });
    }

    // Matching tipi için özel MatchPairs komponenti
    if (activeLessonContent?.type === 'matching' && activeLessonContent?.pairs) {
      generatedExercises.push({
        type: 'matchPairs',
        data: {
          instruction: activeLessonContent.instruction || 'Eşleşen çiftleri bulun',
          pairs: activeLessonContent.pairs
        }
      });
    }

    // Speaking tipi için AI Konuşma Pratiği
    if (lesson?.type === 'speaking' || activeLessonContent?.type === 'speaking') {
      generatedExercises.push({
        type: 'speaking',
        data: {
          unitId: parseInt(unitId)
        }
      });
      return generatedExercises;
    }

    // Time Race tipi için Zamanla Yarış egzersizi
    if (lesson?.type === 'timerace' || activeLessonContent?.type === 'timerace') {
      generatedExercises.push({
        type: 'timerace',
        data: {
          title: activeLessonContent?.title || 'Zamanla Yarış!',
          cards: activeLessonContent?.cards || []
        }
      });
      return generatedExercises;
    }

    // Vocabulary tipi için kelime kartları - tüm kelimeleri tek FlashCard'a gönder
    if (activeLessonContent?.type === 'vocabulary' && activeLessonContent?.words) {
      // Tüm kelimeleri cards formatına dönüştür
      const vocabularyCards = activeLessonContent.words.map(word => ({
        front: word.english,
        back: word.turkish,
        pronunciation: word.pronunciation || '',
        example: word.example || '',
        category: word.category || ''
      }));
      
      generatedExercises.push({
        type: 'flashcard',
        data: {
          cards: vocabularyCards
        }
      });
      
      // Sonra quiz soruları ekle
      if (activeLessonContent.quiz) {
        activeLessonContent.quiz.forEach(q => {
          generatedExercises.push({
            type: 'multipleChoice',
            data: {
              question: q.question,
              options: q.options,
              correctAnswer: q.correct,
              explanation: q.explanation || ''
            }
          });
        });
      }
    }

    // DragDrop tipi için sürükle-bırak egzersizi
    if (activeLessonContent?.type === 'dragdrop' && activeLessonContent?.exercises) {
      activeLessonContent?.exercises.forEach(ex => {
        generatedExercises.push({
          type: 'dragDrop',
          data: {
            instruction: 'Kelimeleri doğru sıraya dizin',
            words: ex.words,
            correctOrder: ex.correctOrder,
            translation: ex.translation
          }
        });
      });
    }

    // FillIn tipi için boşluk doldurma egzersizi
    if (activeLessonContent?.type === 'fillin' && activeLessonContent?.exercises) {
      activeLessonContent?.exercises.forEach(ex => {
        generatedExercises.push({
          type: 'fillInBlank',
          data: {
            sentence: ex.sentence,
            correctAnswer: ex.answer,
            hint: ex.hint
          }
        });
      });
    }

    // Scenario tipi için senaryo bazlı egzersiz
    if (activeLessonContent?.type === 'scenario' && activeLessonContent?.scenarios) {
      activeLessonContent.scenarios.forEach(scenario => {
        // Önce diyaloğu göster
        if (scenario.dialogue) {
          generatedExercises.push({
            type: 'multipleChoice',
            data: {
              question: scenario.question,
              options: scenario.options,
              correctAnswer: scenario.correct,
              explanation: `Doğru cevap: ${scenario.options[scenario.correct]}`
            }
          });
        }
      });
    }

    // Audio tipi için ses egzersizi (şimdilik multiple choice olarak)
    if (activeLessonContent?.type === 'audio' && activeLessonContent?.phrases) {
      activeLessonContent?.phrases.slice(0, 3).forEach((phrase, idx) => {
        const wrongOptions = activeLessonContent?.phrases
          .filter((_, i) => i !== idx)
          .slice(0, 3)
          .map(p => p.translation);
        
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: `"${phrase.text}" ne demek?`,
            options: [...wrongOptions, phrase.translation].sort(() => Math.random() - 0.5),
            correctAnswer: [...wrongOptions, phrase.translation].sort(() => Math.random() - 0.5).indexOf(phrase.translation),
            explanation: `Doğru cevap: ${phrase.translation}`
          }
        });
      });
    }

    // Quiz tipi için quiz soruları
    if (activeLessonContent?.type === 'quiz') {
      const exercises = activeLessonContent?.exercises || [];
      const questions = activeLessonContent?.content?.questions || activeLessonContent?.questions || [];
      
      exercises.forEach(exercise => {
        if (exercise.type === 'multipleChoice') {
          generatedExercises.push({
            type: 'multipleChoice',
            data: {
              question: exercise.question,
              options: exercise.options,
              correctAnswer: exercise.correctIndex !== undefined ? exercise.correctIndex : exercise.correct,
              explanation: exercise.explanation
            }
          });
        }
      });
      
      questions.forEach(q => {
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: q.question,
            options: q.options,
            correctAnswer: q.correctIndex !== undefined ? q.correctIndex : q.correct,
            explanation: q.explanation || `Doğru cevap: ${q.options[q.correctIndex !== undefined ? q.correctIndex : q.correct]}`
          }
        });
      });
    }

    // Final Exam tipi için - sections veya questions içinde sorular var
    if (activeLessonContent?.type === 'final_exam') {
      // Format 1: content.sections (A0 Final gibi)
      if (activeLessonContent?.content?.sections) {
        activeLessonContent?.content.sections.forEach(section => {
          // Bölüm başlığını göster
          if (section.title) {
            generatedExercises.push({
              type: 'teaching',
              data: {
                title: section.title,
                content: ''
              }
            });
          }
          // Bölüm sorularını ekle
          if (section.questions && section.questions.length > 0) {
            section.questions.forEach(q => {
              if (q.type === 'multipleChoice') {
                generatedExercises.push({
                  type: 'multipleChoice',
                  data: {
                    question: q.question,
                    options: q.options,
                    correctAnswer: q.correctIndex !== undefined ? q.correctIndex : q.correct,
                    explanation: q.explanation || ''
                  }
                });
              } else if (q.type === 'fillBlank') {
                generatedExercises.push({
                  type: 'fillInBlank',
                  data: {
                    sentence: q.question,
                    correctAnswer: q.answer,
                    hint: ''
                  }
                });
              } else if (q.type === 'matching' && q.pairs) {
                generatedExercises.push({
                  type: 'matchPairs',
                  data: {
                    instruction: q.question || 'Eşleştirme yapın',
                    pairs: q.pairs
                  }
                });
              }
            });
          }
        });
      }
      
      // Format 2: content.questions (C1INT Final gibi - sections yok)
      if (activeLessonContent?.content?.questions && !activeLessonContent?.content?.sections) {
        activeLessonContent?.content.questions.forEach(q => {
          if (q.type === 'multipleChoice') {
            generatedExercises.push({
              type: 'multipleChoice',
              data: {
                question: q.question,
                options: q.options,
                correctAnswer: q.correctIndex !== undefined ? q.correctIndex : q.correct,
                explanation: q.explanation || ''
              }
            });
          } else if (q.type === 'fillBlank' || q.type === 'fillInBlank') {
            generatedExercises.push({
              type: 'fillInBlank',
              data: {
                sentence: q.question || q.sentence,
                correctAnswer: q.answer,
                hint: q.hint || ''
              }
            });
          } else if (q.type === 'matching' && q.pairs) {
            generatedExercises.push({
              type: 'matchPairs',
              data: {
                instruction: q.question || 'Eşleştirme yapın',
                pairs: q.pairs
              }
            });
          }
        });
      }
    }

    // İçerik yoksa veya yetersizse, ders tipine göre varsayılan egzersizler üret
    if (generatedExercises.length < 3 && lesson) {
      const lessonType = lesson.type;
      const lessonTitle = lesson.title;
      
      // Ders başlığına göre alakalı varsayılan sorular üret
      const grammarQuestions = [
        { question: `"${lessonTitle}" konusunda: "I am a student" cümlesinde hangi fiil var?`, options: ['am', 'student', 'I', 'a'], correctAnswer: 0, explanation: '"am" to be fiilinin birinci tekil şahıs halidir.' },
        { question: 'She ___ English every day.', options: ['study', 'studies', 'studying', 'studied'], correctAnswer: 1, explanation: 'He/She/It ile fiil -s/-es alır.' },
        { question: 'They ___ working now.', options: ['is', 'am', 'are', 'be'], correctAnswer: 2, explanation: 'They ile "are" kullanılır.' },
      ];
      
      const aviationQuestions = [
        { question: `"${lessonTitle}" konusunda: "Cabin crew" ne demek?`, options: ['Pilot', 'Kabin ekibi', 'Yolcu', 'Kaptan'], correctAnswer: 1, explanation: 'Cabin crew = Kabin ekibi/memuru' },
        { question: '"Fasten your seat belt" ne demek?', options: ['Koltuktan kalkın', 'Emniyet kemerinizi bağlayın', 'İnebilirsiniz', 'Kapıyı açın'], correctAnswer: 1, explanation: 'Fasten your seat belt = Emniyet kemerinizi bağlayın' },
        { question: '"Welcome on board" ne demek?', options: ['Hoşça kalın', 'İyi yolculuklar', 'Uçağa hoş geldiniz', 'Dikkat'], correctAnswer: 2, explanation: 'Welcome on board = Uçağa hoş geldiniz' },
      ];
      
      // Ders tipine göre varsayılan içerik üret
      if (lessonType === 'flashcard' || lessonType === 'quiz' || lessonType === 'matching') {
        // Havacılık ve gramer karışımı sorular
        const allQuestions = [...grammarQuestions, ...aviationQuestions];
        allQuestions.slice(0, 3).forEach(q => {
          generatedExercises.push({
            type: 'multipleChoice',
            data: q
          });
        });
      }
      
      if (lessonType === 'dragdrop' || lessonType === 'fillin') {
        generatedExercises.push({
          type: 'fillInBlank',
          data: {
            sentence: 'Welcome ___ board!',
            correctAnswer: 'on',
            options: ['on', 'in', 'at', 'to'],
            hint: 'Uçağa hoş geldiniz!'
          }
        });
        generatedExercises.push({
          type: 'fillInBlank',
          data: {
            sentence: 'Please fasten your seat ___.',
            correctAnswer: 'belt',
            options: ['belt', 'bag', 'book', 'box'],
            hint: 'Emniyet kemerinizi bağlayın.'
          }
        });
        generatedExercises.push({
          type: 'fillInBlank',
          data: {
            sentence: 'The flight ___ at 10:30.',
            correctAnswer: 'departs',
            options: ['departs', 'sleeps', 'eats', 'runs'],
            hint: 'Uçuş 10:30\'da kalkıyor.'
          }
        });
      }
      
      if (lessonType === 'scenario') {
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: 'Yolcu "Can I have some water?" derse ne cevap verirsiniz?',
            options: ['No, you cannot.', 'Of course, right away!', 'I don\'t know.', 'Go away.'],
            correctAnswer: 1,
            explanation: 'Kibar cevap: Of course, right away! = Tabii, hemen getiriyorum!'
          }
        });
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: '"Is there a doctor on board?" ne zaman söylenir?',
            options: ['Yemek servisi', 'Tıbbi acil durum', 'Kalkış öncesi', 'İniş sonrası'],
            correctAnswer: 1,
            explanation: 'Tıbbi acil durumda doktor çağrısı yapılır.'
          }
        });
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: '"Please remain seated" ne demek?',
            options: ['Lütfen ayağa kalkın', 'Lütfen oturmaya devam edin', 'Lütfen çıkın', 'Lütfen bekleyin'],
            correctAnswer: 1,
            explanation: 'Please remain seated = Lütfen oturmaya devam edin'
          }
        });
      }
      
      if (lessonType === 'exam') {
        // Sınav tipi için kapsamlı sorular
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: '"I am" kısaltması nedir?',
            options: ["I'm", "I've", "I'll", "I'd"],
            correctAnswer: 0,
            explanation: "I am = I'm"
          }
        });
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: '"She is" için doğru cümle?',
            options: ['She am happy', 'She is happy', 'She are happy', 'She be happy'],
            correctAnswer: 1,
            explanation: 'She is happy - 3. tekil şahıs "is" kullanır.'
          }
        });
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: '"They ___ passengers" boşluğu doldurun.',
            options: ['is', 'am', 'are', 'be'],
            correctAnswer: 2,
            explanation: 'They are passengers - Çoğul için "are" kullanılır.'
          }
        });
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: '"Are you ready?" sorusunun kısa cevabı?',
            options: ['Yes, I is.', 'Yes, I am.', 'Yes, I are.', 'Yes, I be.'],
            correctAnswer: 1,
            explanation: 'Yes, I am. - Kısa cevaplarda özneyle uyumlu fiil kullanılır.'
          }
        });
        generatedExercises.push({
          type: 'multipleChoice',
          data: {
            question: '"Wednesday" hangi gün?',
            options: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe'],
            correctAnswer: 2,
            explanation: 'Wednesday = Çarşamba'
          }
        });
      }
    }

    return generatedExercises;
  }, [activeLessonContent]);

  useEffect(() => {
    const exercises = generateExercisesFromContent();
    setExercises(exercises);
    setLoading(false);
  }, [generateExercisesFromContent]);

  const handleAnswer = useCallback((correct, answer) => {
    if (correct) {
      setCorrectAnswers(prev => prev + 1);
      setXpEarned(prev => prev + 10);
      setStreak(prev => {
        const newStreak = prev + 1;
        // 3+ ardışık doğru için combo sesi
        if (newStreak >= 3 && newStreak % 3 === 0) {
          playCombo();
        }
        return newStreak;
      });
      playXpGain();
    } else {
      setHearts(prev => Math.max(0, prev - 1));
      setStreak(0); // Streak'i sıfırla
    }
    
    setWaitingForNext(true);
  }, [playXpGain, playCombo]);

  const handleNext = () => {
    setWaitingForNext(false);
    
    if (currentStep < exercises.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeLesson();
    }
  };

  const completeLesson = async (skipResultScreen = false) => {
    setIsComplete(true);
    
    // Başarı sesi çal - %100 doğruluk için özel ses
    const accuracy = exercises.length > 0 ? correctAnswers / exercises.length : 0;
    if (accuracy === 1) {
      playPerfect(); // %100 doğruluk
    } else if (nextLessonInfo?.isLevelComplete) {
      playLevelComplete(); // Seviye tamamlama
    } else {
      playLessonComplete(); // Normal ders tamamlama
    }
    
    // FlashCard kendi completion ekranını gösteriyor, bu yüzden skipResultScreen kullanıyoruz
    if (!skipResultScreen) {
      setShowResults(true);
    }
    
    try {
      const endpoint = isKariyer ? `${API}/kariyer/lesson/complete` : `${API}/program/lesson/complete`;
      const response = await axios.post(endpoint, {
        level_id: levelId,
        section_id: sectionId,
        unit_id: parseInt(unitId),
        lesson_id: parseInt(lessonId),
        xp_earned: xpEarned,
        duration: lesson?.duration || 10,
        correct_answers: correctAnswers,
        total_questions: exercises.length
      }, { withCredentials: true });
      
      // Tekrar sayısını kaydet
      if (response.data?.completion_count) {
        setCompletionCount(response.data.completion_count);
      }
    } catch (error) {
      console.error('Error completing lesson:', error);
    }
  };

  const renderExercise = () => {
    if (exercises.length === 0) return null;
    
    const exercise = exercises[currentStep];
    
    // Teaching için özel handler - direkt sonraki adıma geç
    const handleTeachingComplete = () => {
      setXpEarned(prev => prev + 5);
      if (currentStep < exercises.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        completeLesson();
      }
    };
    
    switch (exercise.type) {
      case 'teaching':
        return (
          <Teaching
            key={currentStep}
            {...exercise.data}
            onAnswer={handleTeachingComplete}
          />
        );
      
      case 'multipleChoice':
        return (
          <MultipleChoice
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
            disabled={waitingForNext}
          />
        );
      
      case 'fillInBlank':
        return (
          <FillInBlank
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
            disabled={waitingForNext}
          />
        );
      
      case 'dragDrop':
        return (
          <DragDropSentence
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
            disabled={waitingForNext}
          />
        );
      
      case 'matchPairs':
        return (
          <MatchPairs
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
            disabled={waitingForNext}
          />
        );
      
      case 'video':
        return (
          <VideoLesson
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
            disabled={waitingForNext}
          />
        );
      
      case 'flashcard':
        // mixed_lesson içinde FlashCard: completion ekranı yerine sonraki egzersize geç
        const isLastExercise = currentStep >= exercises.length - 1;
        const isMixedLesson = exercises.length > 1 && exercises.some(e => e.type === 'teaching' || e.type === 'multipleChoice');
        
        if (isMixedLesson && !isLastExercise) {
          // mixed_lesson içinde ve son egzersiz değil - completion ekranı gösterme
          return (
            <FlashCard
              key={currentStep}
              {...exercise.data}
              onAnswer={handleAnswer}
              disabled={waitingForNext}
              onComplete={() => {
                // FlashCard bitince sonraki egzersize geç
                if (currentStep < exercises.length - 1) {
                  setCurrentStep(prev => prev + 1);
                } else {
                  completeLesson(true);
                }
              }}
              // Navigasyon butonlarını gösterme
              nextLessonInfo={null}
              onNextLesson={null}
              onBackToUnit={null}
            />
          );
        }
        
        // Tek başına FlashCard dersi - normal davranış
        return (
          <FlashCard
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
            disabled={waitingForNext}
            onComplete={() => completeLesson(true)}
            nextLessonInfo={nextLessonInfo}
            onNextLesson={goToNextLesson}
            onBackToUnit={() => window.location.href = isKariyer ? `/level/${levelId}` : `/learn/${sectionId}`}
          />
        );
      
      case 'swipeVocabulary':
        // SwipeVocabularyCard - Sola/Sağa kaydırmalı kelime kartları
        const isLastSwipe = currentStep >= exercises.length - 1;
        const isMixedLessonSwipe = exercises.length > 1 && exercises.some(e => e.type === 'teaching' || e.type === 'multipleChoice');
        
        if (isMixedLessonSwipe && !isLastSwipe) {
          return (
            <SwipeVocabularyCard
              key={currentStep}
              {...exercise.data}
              onAnswer={handleAnswer}
              disabled={waitingForNext}
              onComplete={() => {
                if (currentStep < exercises.length - 1) {
                  setCurrentStep(prev => prev + 1);
                } else {
                  completeLesson(true);
                }
              }}
              nextLessonInfo={null}
              onNextLesson={null}
              onBackToUnit={null}
            />
          );
        }
        
        return (
          <SwipeVocabularyCard
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
            disabled={waitingForNext}
            onComplete={() => completeLesson(true)}
            nextLessonInfo={nextLessonInfo}
            onNextLesson={goToNextLesson}
            onBackToUnit={() => window.location.href = isKariyer ? `/level/${levelId}` : `/learn/${sectionId}`}
          />
        );
      
      case 'simpleSpeaking':
        return (
          <SimpleSpeaking
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
            disabled={waitingForNext}
          />
        );
      
      case 'speaking':
        return (
          <SpeakingPractice
            key={currentStep}
            unitId={parseInt(unitId)}
            onComplete={(result) => {
              if (result.passed) {
                setXpEarned(prev => prev + 50);
                handleAnswer(true);
              }
            }}
          />
        );
      
      case 'timerace':
      case 'timeRace':
        return (
          <VocabularyTimeRace
            key={currentStep}
            {...exercise.data}
            onAnswer={(passed, accuracy) => {
              if (passed) {
                setXpEarned(prev => prev + 50);
              }
              handleAnswer(passed);
            }}
            disabled={waitingForNext}
          />
        );
      
      case 'visualCards':
        return (
          <VisualCards
            key={currentStep}
            {...exercise.data}
            onAnswer={handleTeachingComplete}
          />
        );
      
      case 'storyMode':
        return (
          <StoryMode
            key={currentStep}
            {...exercise.data}
            onAnswer={handleTeachingComplete}
          />
        );
      
      case 'dialogueSimulation':
        return (
          <DialogueSimulation
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
          />
        );
      
      case 'wordHunt':
        return (
          <WordHunt
            key={currentStep}
            {...exercise.data}
            onAnswer={handleAnswer}
          />
        );
      
      case 'memoryTrick':
        return (
          <MemoryTrick
            key={currentStep}
            {...exercise.data}
            onAnswer={handleTeachingComplete}
          />
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!lesson || (!section && !level)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-800 text-center">
          <p className="text-lg">Ders bulunamadı</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4 bg-blue-500 hover:bg-blue-600">
            Ana Sayfaya Dön
          </Button>
        </div>
      </div>
    );
  }

  // Sonuç ekranı
  if (showResults) {
    const accuracy = exercises.length > 0 ? Math.round((correctAnswers / exercises.length) * 100) : 0;
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-500 to-blue-600 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center shadow-2xl">
          <div className="mb-6">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-14 h-14 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Ders Tamamlandı! 🎉</h1>
            <p className="text-gray-500">{lesson.title}</p>
            {completionCount > 1 && (
              <div className="mt-2 inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                <RotateCcw className="w-4 h-4" />
                {completionCount}. Tekrar
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-yellow-50 rounded-xl p-4">
              <Zap className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-800">+{xpEarned}</p>
              <p className="text-xs text-gray-500">Mil</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4">
              <Star className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-800">{accuracy}%</p>
              <p className="text-xs text-gray-500">Doğruluk</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4">
              <Heart className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-800">{hearts}</p>
              <p className="text-xs text-gray-500">Kalan Can</p>
            </div>
          </div>
          
          {/* Sonraki Ders Bilgisi */}
          {nextLessonInfo && !nextLessonInfo.isLevelComplete && (
            <div className="bg-blue-50 rounded-xl p-4 mb-4">
              <p className="text-xs text-blue-600 font-medium mb-1">
                {nextLessonInfo.isNewUnit ? `📚 Yeni Ünite: ${nextLessonInfo.unitTitle}` : 'Sonraki Ders'}
              </p>
              <p className="text-sm font-bold text-blue-800">{nextLessonInfo.lesson?.title}</p>
            </div>
          )}
          
          <div className="space-y-3">
            {/* Sonraki Derse Geç Butonu */}
            <Button
              onClick={goToNextLesson}
              className="w-full py-6 text-lg font-bold bg-emerald-500 hover:bg-emerald-600 rounded-xl shadow-lg"
            >
              {nextLessonInfo?.isLevelComplete 
                ? 'Seviyeyi Tamamla 🏆' 
                : nextLessonInfo?.isUnitComplete 
                  ? 'Sonraki Üniteye Geç' 
                  : 'Sonraki Derse Geç'
              }
              <ChevronRight className="w-6 h-6 ml-2" />
            </Button>
            
            {/* Üniteye Dön Butonu */}
            <Button
              variant="outline"
              onClick={() => navigate(isKariyer ? `/level/${levelId}` : `/learn/${sectionId}`)}
              className="w-full py-4 border-blue-300 text-blue-600 hover:bg-blue-50 rounded-xl"
            >
              Üniteye Dön
            </Button>
            
            {/* Tekrar Et Butonu */}
            <Button
              variant="outline"
              onClick={() => {
                setCurrentStep(0);
                setHearts(5);
                setXpEarned(0);
                setCorrectAnswers(0);
                setIsComplete(false);
                setShowResults(false);
                setWaitingForNext(false);
                const newExercises = generateExercisesFromContent();
                setExercises(newExercises);
              }}
              className="w-full py-4 border-gray-300 text-gray-600 hover:bg-gray-100 rounded-xl"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Tekrar Et
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const progress = exercises.length > 0 ? ((currentStep + 1) / exercises.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-gray-200 shadow-sm">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.location.href = isKariyer ? `/level/${levelId}` : `/learn/${sectionId}`}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
            
            <div className="flex-1">
              <Progress value={progress} className="h-3" />
            </div>
            
            <div className="flex items-center gap-3">
              {/* Ses Ayarları */}
              <SoundSettings />
              
              {/* Streak Göstergesi */}
              {streak >= 3 && (
                <div className="flex items-center gap-1 bg-orange-50 px-3 py-1.5 rounded-full animate-pulse">
                  <span className="text-orange-500 font-bold">🔥 {streak}</span>
                </div>
              )}
              
              <div className="flex items-center gap-1 bg-red-50 px-3 py-1.5 rounded-full">
                <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                <span className="text-red-500 font-bold">{hearts}</span>
              </div>
              <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1.5 rounded-full">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span className="text-yellow-500 font-bold">+{xpEarned}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exercise Area */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-8">
          {renderExercise()}
        </div>
        
        {/* Continue Button - FlashCard için gizle çünkü kendi navigation'ı var */}
        {waitingForNext && exercises[currentStep]?.type !== 'flashcard' && (
          <div className="bg-white border-t border-gray-200 p-4 shadow-lg">
            <div className="max-w-2xl mx-auto">
              <Button
                onClick={handleNext}
                className="w-full py-6 text-lg font-bold bg-blue-500 hover:bg-blue-600 rounded-xl shadow-lg"
              >
                Devam Et <ChevronRight className="w-6 h-6 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
